import transpileTypeScript from '../shared/transpileTypeScript'
import { collectAssetReferencesFromRenderData } from '../shared/assetReferences'
import type { RenderData } from '../assetTypes'
import type { JsonObject } from '../shared/jsonValues'

const stripModuleSyntax = (code: string) =>
  code
    .replace(/import\s+[^;]+['"]phaser['"];?/gi, '')
    .replace(/export\s+default/g, '')
    .replace(/export\s+\{[^}]+\};?/gi, '')

type BuildLearningTemplateHtmlOptions = {
  initialParams?: JsonObject
}

export const buildLearningTemplateHtml = (content: string, options?: BuildLearningTemplateHtmlOptions) => {
  const compiled = transpileTypeScript(stripModuleSyntax(content || ''))
  const wrapped = `const container = document.getElementById('phaser-container');\n${compiled}`
  const hasStaticParams = options?.initialParams !== undefined
  const serializedStaticParams = JSON.stringify(options?.initialParams ?? null)

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      body {
        margin: 0;
        background-color: #ffffff;
        color: #111827;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        display: flex;
        flex: 1;
        min-height: 100vh;
        padding: 0;
      }
    </style>
  </head>
  <body>
    <div id="phaser-container" style="width: 100%; height: 100%;"></div>
    <script src="https://cdn.jsdelivr.net/npm/phaser@3.60.0/dist/phaser-arcade-physics.min.js"></script>
    <script>
      const trellis = window.trellis
      const renderLog = (message) => {
        trellis.log(message)
      }
      const cloneJson = (value) => {
        if (Array.isArray(value)) {
          return value.map((entry) => cloneJson(entry))
        }
        if (value && typeof value === 'object') {
          const cloned = {}
          Object.keys(value).forEach((key) => {
            cloned[key] = cloneJson(value[key])
          })
          return cloned
        }
        return value
      }
      const staticParams = ${serializedStaticParams}
      const staticParamKeys =
        staticParams && typeof staticParams === 'object' ? Object.keys(staticParams) : []

      if (${JSON.stringify(hasStaticParams)}) {
        trellis.params = {
          ...(trellis.params && typeof trellis.params === 'object' ? trellis.params : {}),
          get: () => cloneJson(staticParams || {}),
        }

        if (trellis.runtime && typeof trellis.runtime === 'object') {
          trellis.runtime.registerParamsListener = (listener) => {
            if (typeof listener !== 'function') {
              throw new Error('window.trellis.runtime.registerParamsListener requires a function.')
            }
            queueMicrotask(() => {
              listener({
                params: cloneJson(staticParams || {}),
                previousParams: null,
                changedKeys: [...staticParamKeys],
                revision: 1,
                instanceId: trellis.instanceId,
                isInitial: true,
              })
            })
            return {
              name: 'onParamsChanged',
              params: ['event'],
              description: 'Called when params are updated by the host.',
              source: 'runtime',
            }
          }
        }
      }
      renderLog('Render runtime initialized')
    </script>
    <script>
      (function () {
        const trellis = window.trellis
        const renderLog = (message) => {
          trellis.log(message)
        }
        let started = false
        const executeLearningTemplate = () => {
          if (started) return
          started = true
          try {
            const execute = new Function('Phaser', ${JSON.stringify(wrapped)})
            execute(window.Phaser)
            renderLog('Learning template render executed')
          } catch (error) {
            renderLog('Learning template render failed: ' + (error && error.message ? error.message : error))
            document.body.innerHTML = '<div style="padding:16px;font-family:monospace;color:#b91c1c">' +
              'Failed to render asset. ' + (error && error.message ? error.message : error) +
              '</div>'
          }
        }

        if (${JSON.stringify(hasStaticParams)}) {
          executeLearningTemplate()
          return
        }

        const runtime = trellis && trellis.runtime
        if (runtime && typeof runtime.registerParamsListener === 'function') {
          const timeoutId = window.setTimeout(() => {
            renderLog('Learning template params were not delivered before timeout. Starting with current params.')
            executeLearningTemplate()
          }, 300)

          runtime.registerParamsListener(() => {
            window.clearTimeout(timeoutId)
            executeLearningTemplate()
          })

          return
        }

        try {
          executeLearningTemplate()
        } catch {
          // executeLearningTemplate already handles error reporting.
        }
      })()
    </script>
  </body>
</html>`
}

const learningTemplatesRuntime = (content: string) => buildLearningTemplateHtml(content)

export default learningTemplatesRuntime

export const collectAssetReferences = (content: string, renderData?: RenderData) =>
  collectAssetReferencesFromRenderData(content, renderData)
